import React, { useState } from 'react';
import { basename } from 'node:path';
import type { FC } from 'react';
import { AuthProvider, SiteAuthProvider } from '@wix/cli-auth';
import { Box, Spinner, useAsync } from '@wix/cli-ui-kit';
import { pathExists } from '@wix/cli-fs';
import { getWixConfigFilePath } from '@wix/cli-core-definitions';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { match } from 'variant';
import startCase from 'lodash/startCase.js';
import { CreateProject } from './CreateProject.js';
import { CreateBusiness } from './CreateBusiness.js';
import type { ProjectData } from '../hooks/create-project.js';
import { validateBusinessName } from '../validations/validate-business-name.js';
import { WriteConfig } from './WriteConfig.js';

const BLANK_PROJECT_TEMPLATE_ID = '212b41cb-0da6-4401-9c72-7c579e6477a2';

export const InitHeadlessSiteCommand: FC<{
  targetParentFolder: string;
  onComplete: (result: { msid: string; appId: string }) => void;
}> = ({ targetParentFolder, onComplete }) => {
  const [businessId, setBusinessId] = useState<string>();
  const [projectData, setProjectData] = useState<ProjectData>();

  const { status } = useAsync(async () => {
    if (await pathExists(getWixConfigFilePath(targetParentFolder))) {
      throw new CliError({
        code: CliErrorCode.ProjectIsAlreadyLinked(),
        cause: null,
      });
    }

    const name = basename(targetParentFolder).trim();
    const businessName = startCase(name).slice(0, 255);

    try {
      validateBusinessName(businessName);
    } catch (error) {
      throw new CliError({
        code: CliErrorCode.BusinessNameArgumentIsInvalid({
          errorMessage: (error as Error).message,
        }),
        cause: undefined,
      });
    }

    return { businessName };
  }, []);

  return match(status, {
    Error: () => null,
    Loading: () => <Spinner text="Loading..." />,
    Success: ({ result: { businessName } }) => (
      <Box
        flexDirection="column"
        borderStyle="single"
        borderColor="gray"
        borderBottom={false}
        borderRight={false}
        borderTop={false}
        paddingLeft={1}
      >
        <AuthProvider>
          <CreateBusiness
            businessName={businessName}
            businessTemplateId={BLANK_PROJECT_TEMPLATE_ID}
            onDone={({ businessId }) => setBusinessId(businessId)}
          />
        </AuthProvider>

        {businessId && (
          <SiteAuthProvider siteId={businessId}>
            <CreateProject
              businessId={businessId}
              businessName={businessName}
              onSubmit={(data) => setProjectData(data)}
            />
          </SiteAuthProvider>
        )}

        {businessId && projectData && (
          <WriteConfig
            businessId={businessId}
            projectData={projectData}
            targetParentFolder={targetParentFolder}
            onDone={() =>
              onComplete({
                msid: businessId,
                appId: projectData.projectId,
              })
            }
          />
        )}
      </Box>
    ),
  });
};
